import { Button, Modal } from "@heroui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

export interface ImageGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  images: string[];
}

export function ImageGalleryModal({
  isOpen,
  onClose,
  name,
  images,
}: ImageGalleryModalProps) {
  const [index, setIndex] = useState(0);

  const close = () => {
    setIndex(0);
    onClose();
  };

  return (
    <Modal.Backdrop
      isOpen={isOpen}
      variant="opaque"
      onOpenChange={(open) => !open && close()}
    >
      <Modal.Container placement="center">
        <Modal.Dialog className="w-full max-w-lg">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>Product Image</Modal.Heading>
            <p className="text-muted max-w-md truncate text-sm">{name}</p>
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-3 p-1">
            <div className="bg-surface-secondary relative flex items-center justify-center rounded-2xl p-4">
              <img
                alt={name}
                className="max-h-[50vh] max-w-full rounded-xl object-contain"
                src={images[index] || "/placeholder.png"}
              />
              {images.length > 1 ? (
                <>
                  <Button
                    isIconOnly
                    aria-label="Previous image"
                    className="absolute start-2 top-1/2 -translate-y-1/2"
                    isDisabled={index === 0}
                    variant="secondary"
                    onPress={() => setIndex((i) => Math.max(0, i - 1))}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <Button
                    isIconOnly
                    aria-label="Next image"
                    className="absolute end-2 top-1/2 -translate-y-1/2"
                    isDisabled={index === images.length - 1}
                    variant="secondary"
                    onPress={() =>
                      setIndex((i) => Math.min(images.length - 1, i + 1))
                    }
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </>
              ) : null}
            </div>
            {images.length > 1 ? (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={`${img}-${i}`}
                    aria-label={`Show image ${i + 1}`}
                    className={`size-14 shrink-0 cursor-pointer overflow-hidden rounded-xl outline-2 ${
                      i === index
                        ? "outline-(--accent)"
                        : "opacity-60 outline-transparent hover:opacity-100"
                    }`}
                    type="button"
                    onClick={() => setIndex(i)}
                  >
                    <img alt="" className="size-full object-cover" src={img} />
                  </button>
                ))}
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onPress={close}>
              Close
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
